import React, { createContext, useContext, useMemo } from 'react';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';

import { buildSalonPublicTheme } from '../theme/salonPublicTheme';
import { buildSalonPublicLayout } from '../theme/salonPublicLayout';
import {
  DEFAULT_SALON_PUBLIC_TEMPLATE_KEY,
  getSalonPublicTemplate,
} from '../theme/salonPublicTemplates';


const FALLBACK_THEME = {
  ...buildSalonPublicTheme({}, 'ltr'),
  layout: buildSalonPublicLayout({}, 'ltr'),
  templateKey: DEFAULT_SALON_PUBLIC_TEMPLATE_KEY,
};
const SalonPublicThemeContext = createContext(null);

export const useSalonPublicTheme = () => (
  useContext(SalonPublicThemeContext) || FALLBACK_THEME
);

export const SalonPublicThemeProvider = ({
  config,
  layoutConfig,
  templateKey,
  direction: directionOverride,
  children,
}) => {
  const parentTheme = useTheme();
  const direction = (
    directionOverride || parentTheme.direction
  ) === 'rtl' ? 'rtl' : 'ltr';
  const template = useMemo(
    () => getSalonPublicTemplate(templateKey),
    [templateKey],
  );
  const salonTheme = useMemo(
    () => buildSalonPublicTheme(config || {}, direction),
    [config, direction],
  );
  const salonLayout = useMemo(
    () => buildSalonPublicLayout(layoutConfig || {}, direction),
    [layoutConfig, direction],
  );
  const appearance = useMemo(
    () => ({ ...salonTheme, layout: salonLayout, templateKey: template.key }),
    [salonLayout, salonTheme, template],
  );
  const muiTheme = useMemo(
    () => createTheme(parentTheme, salonTheme.muiOverrides),
    [parentTheme, salonTheme],
  );

  return (
    <SalonPublicThemeContext.Provider value={appearance}>
      <ThemeProvider theme={muiTheme}>{children}</ThemeProvider>
    </SalonPublicThemeContext.Provider>
  );
};

export default SalonPublicThemeContext;
